import { App } from '@/App';
import { initAppInsights, trackException } from '@/lib/app-insights';
import { getUserManager, initOidc } from '@/lib/oidc';
import { restoreDeepLink } from '@/lib/restore-deep-link';
import '@fontsource-variable/geist-mono';
import '@fontsource-variable/outfit';
import React from 'react';
import ReactDOM from 'react-dom/client';
import '@/styles/globals.css';

// Initialise App Insights as early as possible so the global error +
// unhandledrejection listeners are armed before any user code runs. The
// helper is a silent no-op when `VITE_APPLICATIONINSIGHTS_CONNECTION_STRING`
// is unset (local dev / preview deploys), so calling it unconditionally is
// safe.
initAppInsights();

const rootElement = document.getElementById('root');
if (!rootElement) {
  throw new Error('Missing #root element in index.html');
}
const root: HTMLElement = rootElement;

// OIDC bootstrap — must complete before first render so the active user is
// resolved (either freshly set by the `/auth/callback` code exchange after a
// Ping round-trip, or pulled from the sessionStorage cache on warm load) and
// `initOidc` has wired the singleton the API mutator pulls tokens from via
// `getAccessToken()`.
async function main(): Promise<void> {
  await initOidc();
  // Restore the deep link packed into OIDC `state` before the Ping round-trip.
  const user = await getUserManager().getUser();
  restoreDeepLink(user?.state);

  ReactDOM.createRoot(root).render(
    <React.StrictMode>
      <App />
    </React.StrictMode>,
  );
}

/**
 * A rejected `signinRedirectCallback()` (expired/replayed code, `state`
 * mismatch, `?error=access_denied` from Ping) previously left the user
 * stranded on a blank page — `main()` had no `.catch` and nothing else
 * renders into `#root` on that path. Render a minimal, dependency-free
 * fallback (no router, no App — those never mounted) with a way back to a
 * fresh sign-in.
 */
function renderOidcFailureFallback(err: unknown): void {
  trackException(err instanceof Error ? err : new Error(String(err)), {
    errorCode: 'oidc_bootstrap_failed',
  });
  root.innerHTML = '';
  const container = document.createElement('div');
  container.style.cssText =
    'display:flex;flex-direction:column;align-items:center;justify-content:center;' +
    'height:100vh;gap:1rem;font-family:sans-serif;text-align:center;padding:1rem;';
  const message = document.createElement('p');
  message.textContent = 'Sign-in failed. Please try again.';
  const link = document.createElement('a');
  link.href = '/';
  link.textContent = 'Return to Marketplace';
  container.append(message, link);
  root.append(container);
}

main().catch(renderOidcFailureFallback);
